/**
 * Utility functions for Events tracking
 */

/**
 * Extract position coordinates from an event object
 * @param {Object} eventData - The event data object
 * @returns {Object|null} - Position object with lat, lng, and is_valid properties, or null if invalid
 */
export const getEventPosition = (eventData) => {
  let position = null;

  if (!eventData) return null;

  // Handle geofence entry/exit events
  if (
    eventData?.events?.type === "geofence" &&
    ["exit", "entry"].includes(eventData?.events?.status)
  ) {
    const positionType = eventData.events.status === "entry" ? "entry_position" : "exit_position";
    position = eventData.events[positionType];
  }
  // Handle geofence time limit exceeded events
  else if (
    eventData?.events?.type === "geofence" &&
    eventData?.events?.sub_type === "time limit exceeded"
  ) {
    position = eventData.events.start_position;
  }
  // Handle standard events
  else {
    position = eventData?.gps?.position;
  }

  // Validate coordinates
  if (
    position &&
    typeof position.lat === "number" &&
    typeof position.lng === "number" &&
    !isNaN(position.lat) &&
    !isNaN(position.lng) &&
    position.lat >= -90 &&
    position.lat <= 90 &&
    position.lng >= -180 &&
    position.lng <= 180
  ) {
    return {
      lat: position.lat,
      lng: position.lng,
      is_valid: true,
      address: position.address,
    };
  }

  return null;
};

/**
 * Extract all valid positions from events array
 * @param {Array} events - Array of event objects
 * @returns {Array} - Array of valid position objects
 */
export const getAllEventPositions = (events) => {
  if (!Array.isArray(events)) return [];

  return events
    .map((event, index) => {
      const position = getEventPosition(event);
      return position ? { ...position, eventIndex: index, eventData: event } : null;
    })
    .filter(Boolean);
};

/**
 * Calculate bounds for multiple positions
 * @param {Array} positions - Array of position objects with lat/lng
 * @returns {Object|null} - Bounds object with north, south, east, west properties, or null if no valid positions
 */
export const calculateBounds = (positions) => {
  if (!Array.isArray(positions) || positions.length === 0) return null;

  const bounds = {
    north: -90,
    south: 90,
    east: -180,
    west: 180,
  };

  positions.forEach((position) => {
    if (position.lat > bounds.north) bounds.north = position.lat;
    if (position.lat < bounds.south) bounds.south = position.lat;
    if (position.lng > bounds.east) bounds.east = position.lng;
    if (position.lng < bounds.west) bounds.west = position.lng;
  });

  return bounds;
};

/**
 * Create a Leaflet geofence layer based on geofence data
 * @param {Object} geofenceData - The geofence data object
 * @param {string} geofenceId - Unique identifier for the geofence
 * @param {Object} options - Additional options for the geofence layer
 * @returns {L.Layer|null} - Leaflet layer object or null if creation fails
 */
export const createGeofenceLayer = (geofenceData, layerName, options = {}) => {
  if (!geofenceData) {
    return null;
  }

  const { type, radius } = geofenceData;

  const defaultOptions = {
    color: "#28a745", // Green color for geofences
    weight: 4,
    fillOpacity: 0.4,
    opacity: 0.8,
    name: layerName,
    ...options,
  };

  let geofenceLayer = null;

  try {
    const coordinates =
      Object.keys(geofenceData).length > 0 && geofenceData?.coordinates
        ? geofenceData?.coordinates[0]?.map((cord) => [cord[1], cord[0]])
        : [];
    if (type === "Circle" && radius) {
      geofenceLayer = L.circle(coordinates, {
        radius: radius,
        ...defaultOptions,
      });
    } else if (type === "Polygon" && coordinates && coordinates.length > 0) {
      geofenceLayer = L.polygon(coordinates, {
        ...defaultOptions,
      });
    }
  } catch (error) {
    console.error(`Error creating geofence layer for ${geofenceId}:`, error);
    return null;
  }

  return geofenceLayer;
};

/**
 * Fit map bounds to multiple geofences and markers (overview mode)
 * @param {L.Map} map - The Leaflet map instance
 * @param {Array} geofenceLayers - Array of geofence layers
 * @param {Array} markers - Array of markers
 * @param {Object} fallbackPosition - Fallback position if bounds fitting fails
 * @param {number} fallbackZoom - Fallback zoom level
 * @param {number} delay - Delay in milliseconds before fitting bounds (default: 200)
 * @returns {Function} - Cleanup function to clear timeout
 */
export const fitMapBoundsToGeofences = (
  map,
  geofenceLayers = [],
  markers = [],
  fallbackPosition = [20, 0],
  fallbackZoom = 2,
  delay = 200
) => {
  const marker = markers[0]
  const geofenceLayer = geofenceLayers[0]

  if (!map || !marker || !geofenceLayer) {
    // Fallback to marker only view
    if (map && marker) {
      try {
        if (map.getContainer()) {
          map.setView(marker.getLatLng(), 14);
        }
      } catch (e) {
        console.warn("Error setting map view to marker:", e);
      }
    }
    return () => {}; // Return empty cleanup function
  }

  const fallbackToMarkerView = () => {
    if (map && map.getContainer()) {
      if (fallbackPosition) {
        map.setView(fallbackPosition, fallbackZoom);
      } else {
        map.setView(marker.getLatLng(), fallbackZoom);
      }
    }
  }

  let timeoutId = null;

  try {
    timeoutId = setTimeout(() => {
      try {
        if (map && map.getContainer() && map._mapPane) {
          // Force map invalidation first
          map.invalidateSize();

          // Create bounds manually to ensure all layers are included
          let combinedBounds = L.latLngBounds([]);

          // Add all marker bounds
          markers.forEach((marker) => {
            if (marker && marker.getLatLng) {
              combinedBounds.extend(marker.getLatLng());
            }
          });

          // Add all geofence bounds
          geofenceLayers.forEach((layer) => {
            if (layer && layer.getBounds) {
              const layerBounds = layer.getBounds();
              if (layerBounds && layerBounds.isValid()) {
                combinedBounds.extend(layerBounds);
              }
            }
          });

          // Check if combined bounds are valid
          if (combinedBounds && combinedBounds.isValid()) {
            // Calculate dynamic padding based on the bounds size
            const boundsSize = combinedBounds
              .getNorthEast()
              .distanceTo(combinedBounds.getSouthWest());

            // More conservative padding calculation for multiple geofences
            let paddingValue;
            if (boundsSize < 1000) {
              // Small area (< 1km)
              paddingValue = 50;
            } else if (boundsSize < 5000) {
              // Medium area (< 5km)
              paddingValue = 30;
            } else {
              // Large area
              paddingValue = 20;
            }

            map.fitBounds(combinedBounds, {
              padding: [paddingValue, paddingValue],
              maxZoom: boundsSize < 500 ? 18 : boundsSize < 2000 ? 16 : 14, // More conservative zoom for overview
              animate: true,
              duration: 0.3,
            });
          } else {
            // Fallback to marker view if bounds are invalid
            fallbackToMarkerView()
          }
        }
      } catch (innerError) {
        console.warn("Error in delayed geofence bounds fitting:", innerError);
        fallbackToMarkerView()
      }
    }, delay);
  } catch (e) {
    console.warn("Error fitting geofence bounds:", e);
    fallbackToMarkerView()
  }

  // Return cleanup function
  return () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };
};
